// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Compass (#191) — collection catalog.
//
// The single source of truth for which collections the in-app Compass data
// browser lists, what they're called, and how to search them on each backend.
//
// Two backends, two search realities:
//   - MONGO  → server-side find on ANY field. `mongoField` is the real Mongo
//              field path; the browser can filter on anything via the raw
//              filter box, and the quick-search fields are just conveniences.
//   - CONVEX → only INDEXED columns can be searched server-side (Convex has no
//              ad-hoc field scan within the 16 MB read budget). `convexField`
//              names the indexed column (must match SEARCH_INDEXES in
//              convex/compass.ts) or is null when that field isn't indexed —
//              in which case the browser filters the loaded page client-side.
//
// `convexTable` is null for collections NOT mirrored into Convex yet (Crews,
// EventLog, AuditSnapshots, BlackoutDatesAudit, LaborCatalog). The browser
// shows those as "Mongo-only" and disables the Convex backend for them. This
// is exactly the surface that makes the "Convex should mirror EVERYTHING"
// gap visible (see #191 discussion).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

export const COMPASS_COLLECTIONS = [
  {
    key: "activeQueue",
    label: "ActiveQueue (Jobs)",
    mongoCollection: "ActiveQueue",
    convexTable: "activeQueue",
    // Grid display columns (distinct from quickSearch). No _id column.
    columns: [
      { label: "Invoice #", path: "invoiceNo" },
      { label: "Company", path: "companyCode" },
      { label: "Location", path: "location" },
      { label: "Installer", path: "installer" },
      { label: "Status", path: "statusCode" },
      { label: "Address", path: "customer_address" },
    ],
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: "mongoId" },
      { label: "Invoice #", mongoField: "invoiceNo", convexField: null },
      { label: "Company", mongoField: "companyCode", convexField: "companyCode" },
      { label: "Location", mongoField: "location", convexField: null },
      { label: "Installer (payroll)", mongoField: "installer", convexField: "installer" },
      { label: "Status code", mongoField: "statusCode", convexField: null },
    ],
  },
  {
    key: "candidates",
    label: "Candidates (Installers)",
    mongoCollection: "Candidates",
    convexTable: "candidates",
    columns: [
      { label: "Payroll #", path: "payrollNo" },
      { label: "Company", path: "companyCode" },
      { label: "Location", path: "cLocationCode" },
      { label: "Full name", path: "Name_Full" },
      { label: "RecordType", path: "RecordType" },
    ],
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: "mongoId" },
      { label: "Payroll #", mongoField: "payrollNo", convexField: "payrollNo", format: "payrollNo" },
      { label: "Company", mongoField: "companyCode", convexField: "companyCode" },
      { label: "Location", mongoField: "cLocationCode", convexField: null },
      { label: "Full name", mongoField: "Name_Full", convexField: null },
    ],
  },
  {
    key: "attributes",
    label: "Attributes (Availability)",
    mongoCollection: "Attributes",
    convexTable: "attributes",
    columns: [
      { label: "Company", path: "companyCode" },
      { label: "Location", path: "location" },
      { label: "Payroll #", path: "_kf_payrollNo" },
      { label: "encodedLargeTitle", path: "encodedLargeTitle" },
    ],
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: "mongoId" },
      { label: "Company", mongoField: "companyCode", convexField: "companyCode" },
      { label: "Location", mongoField: "location", convexField: null },
      { label: "Payroll #", mongoField: "_kf_payrollNo", convexField: null },
    ],
  },
  {
    key: "users",
    label: "Users",
    mongoCollection: "Users",
    convexTable: "users",
    columns: [
      { label: "Username", path: "username" },
      { label: "defaultLocation", path: "defaultLocation" },
      { label: "timeTravelAccess", path: "timeTravelAccess" },
    ],
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: "mongoId" },
      { label: "Username (email)", mongoField: "username", convexField: "username" },
      { label: "id", mongoField: "id", convexField: "idField" },
    ],
  },
  {
    key: "offices",
    label: "Offices (Locations)",
    mongoCollection: "Offices",
    convexTable: "offices",
    columns: [
      { label: "Company", path: "companyCode" },
      { label: "Location", path: "Location" },
      { label: "Region", path: "Region" },
      { label: "Active", path: "Active" },
    ],
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: "mongoId" },
      { label: "Company", mongoField: "companyCode", convexField: "companyCode" },
      { label: "Location", mongoField: "Location", convexField: "location" },
    ],
  },
  {
    key: "stores",
    label: "Stores (Lowe's)",
    mongoCollection: "Stores",
    convexTable: "stores",
    columns: [
      { label: "KI location", path: "KI_associatedLocation" },
      { label: "Store #", path: "Store#" },
      { label: "City", path: "City" },
      { label: "State", path: "State" },
      { label: "Region", path: "Region" },
    ],
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: "mongoId" },
      { label: "KI location", mongoField: "KI_associatedLocation", convexField: "kiAssociatedLocation" },
      { label: "Store #", mongoField: "Store#", convexField: null },
    ],
  },
  {
    key: "logs",
    label: "Logs",
    mongoCollection: "Logs",
    convexTable: "logs",
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: "mongoId" },
      { label: "Location", mongoField: "location", convexField: "location" },
      { label: "User", mongoField: "user", convexField: null },
    ],
  },
  // - - - Formerly Mongo-only — now fully mirrored into Convex (#191).
  // Only _id (mongoId) is indexed in Convex; other quick-search fields stay
  // client-side (convexField: null) on the loaded page.
  {
    key: "crews",
    label: "Crews",
    mongoCollection: "Crews",
    convexTable: "crews",
    quickSearch: [{ label: "_id", mongoField: "_id", convexField: "mongoId" }],
  },
  {
    key: "eventLog",
    label: "EventLog (Time Machine)",
    mongoCollection: "EventLog",
    convexTable: "eventLog",
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: "mongoId" },
      { label: "Collection", mongoField: "collection", convexField: null },
      { label: "Operation", mongoField: "operation", convexField: null },
    ],
  },
  {
    key: "auditSnapshots",
    label: "AuditSnapshots (Time Machine)",
    mongoCollection: "AuditSnapshots",
    convexTable: "auditSnapshots",
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: "mongoId" },
      { label: "Source collection", mongoField: "sourceCollection", convexField: null },
    ],
  },
  {
    key: "blackoutDatesAudit",
    label: "BlackoutDatesAudit",
    mongoCollection: "BlackoutDatesAudit",
    convexTable: "blackoutDatesAudit",
    quickSearch: [{ label: "_id", mongoField: "_id", convexField: "mongoId" }],
  },
  {
    key: "laborCatalog",
    label: "LaborCatalog",
    mongoCollection: "LaborCatalog",
    convexTable: "laborCatalog",
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: null },
      { label: "Item #", mongoField: "itemNo", convexField: null },
    ],
  },
  // - - - Operational / audit collections (#201 Tier A + Tier B).
  // Both live in the UAT database (matching the existing ConvexReconcileState
  // convention), so browse from UAT env. Mongo-only — they're operator-side
  // metadata about the Mongo↔Convex mirror, not customer data.
  {
    key: "reconcileAudit",
    label: "ReconcileAudit (Tier A+B)",
    mongoCollection: "ReconcileAudit",
    convexTable: null,
    columns: [
      { label: "Run at", path: "runAt" },
      { label: "Collection", path: "collection" },
      { label: "Mode", path: "mode" },
      { label: "Drift", path: "drift" },
      { label: "Trigger", path: "_triggerVersion" },
    ],
    quickSearch: [
      { label: "_id", mongoField: "_id", convexField: null },
      { label: "Collection", mongoField: "collection", convexField: null },
      { label: "Trigger version", mongoField: "_triggerVersion", convexField: null },
      { label: "Mode", mongoField: "mode", convexField: null },
      { label: "Drift only", mongoField: "drift", convexField: null },
    ],
  },
  {
    key: "triggerVersions",
    label: "TriggerVersions (heartbeat)",
    mongoCollection: "TriggerVersions",
    convexTable: null,
    columns: [
      { label: "Trigger name", path: "_id" },
      { label: "Version", path: "version" },
      { label: "Last run", path: "lastRunAt" },
      { label: "Status", path: "lastRunStatus" },
      { label: "Total runs", path: "totalRuns" },
    ],
    quickSearch: [
      { label: "_id (trigger name)", mongoField: "_id", convexField: null },
      { label: "Version", mongoField: "version", convexField: null },
      { label: "Status", mongoField: "lastRunStatus", convexField: null },
    ],
  },
];

// Lookup by UI key.
export function findCompassCollection(key) {
  return COMPASS_COLLECTIONS.find((c) => c.key === key) || null;
}

// Is this collection available on the given backend?
export function isCollectionAvailable(collection, backend) {
  if (!collection) return false;
  if (backend === "convex") return Boolean(collection.convexTable);
  return Boolean(collection.mongoCollection);
}

// formatPayrollNo — mirrors the query Lambda + convex/lib/shared.ts EXACTLY.
// Needed because Convex stores payrollNo in this formatted (7-char padded)
// form on its indexed column, so a Convex search must format the user's typed
// value first. (Mongo stores the raw value, so Mongo search uses it as-is.)
export function formatPayrollNo(payrollNo) {
  if (payrollNo === null || payrollNo === undefined) return "";
  let formatted = String(payrollNo);
  if (formatted === "") return "";
  const decimalPosition = formatted.indexOf(".");
  if (decimalPosition === formatted.length - 2) {
    formatted = formatted + "0";
  }
  return formatted.padStart(7, "0");
}
